import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Crown,
  MapPin,
  Calendar,
  Clock,
  Users,
  Send,
  Tag,
  Percent,
  Globe,
  Receipt,
  X,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/Navbar";
import axios from "axios";
import Config from "../../Config";
import moment from "moment";
import RenderRecentDealStatus from "../../components/StatusBadge";
import { toast } from "react-toastify";

const StatusToggle = ({ isActive, onToggle, isLoading, dealTitle }) => {
  return (
    <div className="inline-flex relative items-center">
      <button
        onClick={onToggle}
        disabled={isLoading}
        className={`
          relative w-11 h-6 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500
          ${isActive ? 'bg-amber-600' : 'bg-gray-200'}
          ${isLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
        `}
        title={`${isActive ? 'Deactivate' : 'Activate'} ${dealTitle}`}
      >
        <span
          className={`
            absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-300 ease-in-out
            ${isActive ? 'translate-x-5' : 'translate-x-0'}
            ${isLoading ? 'animate-pulse' : ''}
          `}
        />
      </button>
      {isLoading && (
        <div className="flex absolute -right-6 items-center">
          <div className="w-4 h-4 rounded-full border-2 border-amber-600 animate-spin border-t-transparent" />
        </div>
      )}
    </div>
  );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, isLoading }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="relative p-6 m-4 w-full max-w-md bg-white rounded-xl shadow-xl"
      >
        <h3 className="mb-2 text-lg font-semibold text-gray-900">{title}</h3>
        <p className="mb-6 text-gray-600">{message}</p>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex gap-2 items-center px-4 py-2 text-white bg-amber-600 rounded-lg transition-colors hover:bg-amber-700"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                Processing...
              </>
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ViewPremiumDeal = ({premiumDeal}) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ 
    isOpen: false, 
    dealSlug: null,
    currentStatus: null,
    dealTitle: ''
  });

  const PLACEHOLDER_IMAGE =
    "https://placehold.co/600x400/e2e8f0/64748b?text=No+Image+Available";

  useEffect(() => {
    const fetchDeal = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const response = await axios.get(
          `${Config.baseUrl}/only/deals/${slug}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          } 
        );
        
        const data = response.data;
        if (data.data.status_code === "000") {
          setDeal(data.data.data);
        } else {
          setError(data.data.reason || "Failed to fetch deal details");
        }
      } catch (err) {
        console.error("Error fetching deal:", err);
        setError(err.response?.data?.data?.reason || "Failed to fetch deal details");
      } finally {
        setLoading(false);
      }
    };

    if (premiumDeal) {
      setDeal(premiumDeal);
      setLoading(false);
    } else if (slug) {
      fetchDeal();
    }
  }, [premiumDeal, slug]);

  const handleStatusToggle = async (dealSlug, currentStatus, dealTitle) => {
    setConfirmModal({
      isOpen: true,
      dealSlug,
      currentStatus,
      dealTitle
    });
  };

  const handleStatusConfirm = async () => {
    const { dealSlug, currentStatus } = confirmModal;
    
    try {
      setStatusLoading(true);
      
      const token = localStorage.getItem("auth_token");
      const endpoint = currentStatus === 'active' 
        ? `${Config.baseUrl}/deactivate/deal`
        : `${Config.baseUrl}/activate/deal/${dealSlug}`;
      
      const response = await axios.get(
        endpoint,
        {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        }
      );

      const data = await response?.data;

      if (data.data.status_code === "000") {
        setDeal(prevDeal => ({
          ...prevDeal,
          status: currentStatus === 'active' ? 'inactive' : 'active'
        }));
        toast.success('Status updated successfully');
      } else {
        toast.error(data.data.reason || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating deal status:', error);
      toast.error(error.message || 'Failed to update deal status');
    } finally {
      setStatusLoading(false);
      setConfirmModal({ isOpen: false, dealSlug: null, currentStatus: null, dealTitle: '' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="w-12 h-12 rounded-full border-b-2 border-amber-600 animate-spin"></div>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-50">
        <p className="text-red-600">{error || "Deal not found"}</p>
        <button
          onClick={() => navigate("/premium-deals")}
          className="px-4 py-2 mt-4 text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100"
        >
          Back to Premium Deals
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar page="View Premium Deal" />
    
      <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate("/premium-deals")}
          className="flex gap-2 items-center mb-6 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Premium Deals</span>
        </motion.button>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="space-y-6 lg:col-span-2">
            {/* Image and Title Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="overflow-hidden bg-white rounded-2xl shadow-sm"
            >
              <div className="relative aspect-video">
                <img
                  src={deal.deal_image || PLACEHOLDER_IMAGE}
                  alt={deal.title}
                  className="object-cover w-full h-full"
                />
                <div className="absolute top-4 left-4">
                  <span className="inline-flex gap-1 items-center px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-100 rounded-full shadow-sm">
                    <Crown className="w-4 h-4" />
                    Premium Deal
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h1 className="text-2xl font-bold text-gray-900">
                  {deal.title}
                </h1>
                <p className="mt-2 text-gray-600">{deal.description}</p>
              </div>
            </motion.div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Location Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 bg-white rounded-xl shadow-sm"
              >
                <div className="flex gap-4">
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Location</h3>
                    {Array.isArray(deal.location) && deal.location.length > 0 ? (
                      deal.location[0].region === "all" ? (
                        <div>
                          <p className="flex gap-2 items-center mt-1 text-gray-600">
                            <Globe className="w-4 h-4" />
                            <span>Available Nationwide</span>
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {deal.location.map((loc, index) => (
                            <div key={`${loc.region}-${loc.location}-${index}`} className="mt-1">
                              <p className="text-gray-600">{loc.location}</p>
                              <p className="text-sm text-gray-500">{loc.region} Region</p>
                            </div>
                          ))}
                        </div>
                      )
                    ) : (
                      <p className="mt-1 text-gray-500">Location not specified</p>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Category Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 bg-white rounded-xl shadow-sm"
              >
                <div className="flex gap-4">
                  <div className="p-3 bg-purple-50 rounded-xl">
                    <Tag className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Categories</h3>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Array.isArray(deal.category) && deal.category.length > 0 ? (
                        deal.category.map((cat, index) => (
                          <span
                            key={`${cat}-${index}`}
                            className="px-2 py-1 text-sm text-purple-700 bg-purple-50 rounded-full"
                          >
                            {cat}
                          </span>
                        ))
                      ) : deal.category ? (
                        <span className="px-2 py-1 text-sm text-purple-700 bg-purple-50 rounded-full">
                          {deal.category}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">No categories specified</span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Features Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 bg-white rounded-xl shadow-sm"
            >
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Deal Status
                </h3>
                <div className="flex gap-2 items-center">
                  <StatusToggle
                    isActive={deal.status?.toLowerCase() === 'active'}
                    onToggle={() => handleStatusToggle(deal.premium_deal_slug, deal.status, deal.title)}
                    isLoading={statusLoading}
                    dealTitle={deal.title}
                  />
                  <span className="text-sm text-gray-600">
                    {deal.status?.toLowerCase() === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="mt-2">
                <RenderRecentDealStatus status={deal.status} />
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-6 bg-white rounded-xl shadow-sm"
            >
              <div className="space-y-4">
                <div className="flex gap-2 items-baseline">
                  <span className="text-3xl font-bold text-amber-600">
                    ₵{deal.original_price.toLocaleString()}
                  </span>
                  <span className="text-lg text-gray-500 line-through">
                    ₵{deal.original_price.toLocaleString()}
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <Percent className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-green-600">
                    Save {Math.round(((deal.original_price - deal.discounted_price) / deal.original_price) * 100)}% on this deal
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <motion.button
                    disabled={deal.status?.toLowerCase() !== 'active'}
                    onClick={() => navigate(`/premium-deals/${deal.premium_deal_slug}/blast`)}
                    whileHover={deal.status?.toLowerCase() === 'active' ? { scale: 1.02 } : {}}
                    whileTap={deal.status?.toLowerCase() === 'active' ? { scale: 0.98 } : {}}
                    className={`flex gap-2 justify-center items-center text-[13px] py-3 w-full text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 ${deal.status?.toLowerCase() !== 'active' ? 'cursor-not-allowed' : ''}`}
                  >
                    <Send className="w-5 h-5" />
                    Blast SMS
                  </motion.button>
                  <motion.button
                    onClick={() => navigate(`/premium-deals/${deal.premium_deal_slug}/redemptions`)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex gap-2 justify-center items-center py-3 w-full text-[13px] text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100"
                  >
                    <Receipt className="w-5 h-5" />
                    Redemptions
                  </motion.button>
                </div>
              </div>
            </motion.div>

            {/* Stats Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="p-6 bg-white rounded-xl shadow-sm"
            >
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Deal Statistics
              </h3>
              <div className="space-y-4">
                <div className="flex gap-3 items-center p-3 bg-blue-50 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-blue-600">Total Subscribers</p>
                    <p className="text-lg font-semibold text-blue-700">
                      {deal.subscriber_count || 0}
                    </p>
                  </div>
                </div>
                <div 
                  className="flex gap-3 items-center p-3 bg-indigo-50 rounded-lg transition-colors cursor-pointer hover:bg-indigo-100"
                  onClick={() => navigate(`/premium-deals/${deal.id}/redemptions`)}
                >
                  <Send className="w-5 h-5 text-indigo-600" />
                  <div>
                    <p className="text-sm text-indigo-600">Total Sent</p>
                    <p className="text-lg font-semibold text-indigo-700">
                      {deal.initial_blast}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Validity Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="p-6 bg-white rounded-xl shadow-sm"
            >
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Validity Period
              </h3>
              <div className="space-y-3">
                <div className="flex gap-3 items-center">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Valid From</p>
                    <p className="text-gray-900">
                      {moment(deal?.start_date).format("DD/MMM/YYYY")}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 items-center">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Valid Until</p>
                    <p className="text-gray-900">
                      {moment(deal?.end_date).format("DD/MMM/YYYY")}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Add the ConfirmationModal */}
      <AnimatePresence>
        {confirmModal.isOpen && (
          <ConfirmationModal
            isOpen={confirmModal.isOpen}
            onClose={() => setConfirmModal({ isOpen: false, dealSlug: null, currentStatus: null, dealTitle: '' })}
            onConfirm={handleStatusConfirm}
            title="Confirm Status Change"
            message={`Are you sure you want to ${confirmModal.currentStatus === 'active' ? 'deactivate' : 'activate'} "${confirmModal.dealTitle}"?`}
            isLoading={statusLoading}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ViewPremiumDeal;
